import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { BookOpen, Save, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface DailyNotesProps {
  user: User | null;
  roadmapId: string | null;
  dayNumber: number;
  onSignInRequired: () => void;
  className?: string;
}

export const DailyNotes = ({
  user,
  roadmapId,
  dayNumber,
  onSignInRequired,
  className,
}: DailyNotesProps) => {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [noteId, setNoteId] = useState<string | null>(null);

  useEffect(() => {
    const fetchNote = async () => {
      if (!user || !roadmapId) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("roadmap_notes")
          .select("*")
          .eq("user_id", user.id)
          .eq("roadmap_id", roadmapId)
          .eq("day_number", dayNumber)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setNote(data.note_content);
          setNoteId(data.id);
        }
      } catch (error) {
        console.error("Error fetching note:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNote();
  }, [user, roadmapId, dayNumber]);

  const handleSave = async () => {
    if (!user) {
      onSignInRequired();
      return;
    }

    if (!roadmapId || !note.trim()) {
      toast.error("Please write something before saving");
      return;
    }

    setSaving(true);

    try {
      if (noteId) {
        // Update existing note
        const { error } = await supabase
          .from("roadmap_notes")
          .update({
            note_content: note,
            updated_at: new Date().toISOString(),
          })
          .eq("id", noteId);

        if (error) throw error;
      } else {
        // Create new note
        const { data, error } = await supabase
          .from("roadmap_notes")
          .insert({
            user_id: user.id,
            roadmap_id: roadmapId,
            day_number: dayNumber,
            note_content: note,
          })
          .select()
          .single();

        if (error) throw error;
        if (data) setNoteId(data.id);
      }

      toast.success("Note saved successfully! 📝");
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error("Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            <CardTitle className="text-lg">Daily Notes</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Sign in to write and save your daily reflections
          </p>
          <Button onClick={onSignInRequired} className="w-full">
            Sign In to Take Notes
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            <CardTitle className="text-lg">Daily Notes</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading your notes...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5" />
          <CardTitle className="text-lg">Daily Notes & Reflections</CardTitle>
        </div>
        <p className="text-sm text-muted-foreground">
          Write down what you learned, your thoughts, and key takeaways
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What did you learn today? What challenges did you face? What are your key takeaways?"
          className="min-h-[150px] resize-y"
        />
        <Button
          onClick={handleSave}
          disabled={saving || !note.trim()}
          className="w-full"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Notes
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
